/**
 * Firebase Storage Compatibility Layer
 * Maps Firebase Storage API calls to the new R2-backed storage service.
 *
 * Requirements: 6.1
 */
import { uploadImage, getImageUrl } from '../services/storage';
import { IMAGE_URL } from './config';

/** getStorage — returns a proxy object. */
export function getStorage() {
  return { _type: 'r2-storage' };
}

/**
 * ref(storage, path) — creates a storage reference.
 */
export function ref(_storage, path) {
  return new StorageRef(path);
}

class StorageRef {
  constructor(path) {
    this.path = path || '';
    this.name = path ? path.split('/').pop() : '';
    this._fileId = null;

    // Auto-extract file ID from URLs
    if (path && path.includes('/files/')) {
      const match = path.match(/\/files\/([^/?#]+)/);
      if (match) this._fileId = match[1];
    }
  }
}

/**
 * Helper: Extract file ID from a storage URL.
 */
export function extractFileId(url) {
  if (!url || typeof url !== 'string') return null;
  const match = url.match(/\/files\/([^/?#]+)/);
  return match ? match[1] : null;
}

/**
 * Helper: Check if a URL is a storage URL (Appwrite or R2).
 */
export function isAppwriteUrl(url) {
  return typeof url === 'string' && (
    url.includes('cloud.appwrite.io') ||
    url.includes('/buckets/') ||
    url.includes(IMAGE_URL)
  );
}

/**
 * uploadBytes — uploads a file to R2 via the API Worker.
 */
export async function uploadBytes(storageRef, file) {
  const url = await uploadImage(file);
  const fileId = extractFileId(url) || 'unknown';
  storageRef._fileId = fileId;
  return {
    ref: storageRef,
    metadata: {
      fullPath: storageRef.path,
      name: storageRef.name,
      size: file.size,
    },
  };
}

/**
 * getDownloadURL — gets the file URL from a storage reference.
 */
export async function getDownloadURL(storageRef) {
  if (!storageRef._fileId) {
    throw new Error('No file ID available. Upload the file first.');
  }
  return getImageUrl(storageRef._fileId);
}

/**
 * deleteObject — deletes a file from R2 storage.
 */
export async function deleteObject(storageRef) {
  let fileId = storageRef._fileId;
  if (!fileId && typeof storageRef === 'string') {
    fileId = extractFileId(storageRef);
  }
  if (!fileId && storageRef?.path) {
    fileId = extractFileId(storageRef.path);
  }
  if (fileId) {
    const token = await (window).Clerk?.session?.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await fetch(`${process.env.REACT_APP_API_URL || 'https://api.villakosh.in'}/upload/${fileId}`, {
      method: 'DELETE',
      headers,
    });
  } else {
    console.warn('deleteObject: No file ID available for:', storageRef?.path || storageRef);
  }
}

/**
 * Helper: Delete a file by its URL.
 */
export async function deleteByUrl(url) {
  if (!url || typeof url !== 'string') return;
  const fileId = extractFileId(url);
  if (fileId) {
    try {
      const token = await (window).Clerk?.session?.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${process.env.REACT_APP_API_URL || 'https://api.villakosh.in'}/upload/${fileId}`, {
        method: 'DELETE',
        headers,
      });
    } catch (err) {
      console.warn('deleteByUrl failed:', err.message);
    }
  }
}

/**
 * listAll — not supported with R2 public API. Returns empty.
 */
export async function listAll(storageRef) {
  return { items: [], prefixes: [] };
}

/**
 * uploadBytesResumable — same as uploadBytes (no resumable upload with R2).
 */
export async function uploadBytesResumable(storageRef, file) {
  return uploadBytes(storageRef, file);
}

/**
 * Helper: Upload a file and directly get the URL.
 */
export async function uploadAndGetUrl(file) {
  return uploadImage(file);
}
